import Vue from "vue"
import axios from "axios"
import QS from "qs"
import { Message, Loading } from "element-ui"
import LruCache from "./lru-cache"
import storage from "./storage"
import { openPop, reSetLogin, domainAdaptationURL } from "./utils"
import { getCommonParam, getDomainFlag } from "@/plugins/env"
import { Logger } from "@/plugins/logger"

const codeMessage = {
  401: "Unauthorized"
}

let loadingToast = null
let needLoadingRequestCount = 0
let messageIns = null

const showLoading = (message = "Loading") => {
  if (needLoadingRequestCount === 0 && !loadingToast) {
    loadingToast = Loading.service({
      background: "rgba(0, 0, 0, 0)"
    })
  }
  needLoadingRequestCount++
}

const hideLoading = () => {
  if (needLoadingRequestCount <= 0) return
  needLoadingRequestCount--
  if (needLoadingRequestCount === 0 && loadingToast) {
    loadingToast.close()
    loadingToast = null
  }
}

const errorHandler = (error, status) => {
  const message = error.result || error.message || codeMessage[status] || "System Error"
  switch (status) {
    case 401:
      // 处理未授权错误
      break
    default:
      message &&
        Message({
          message,
          type: "error",
          duration: 5 * 1000
        })
  }
}

const baseURL = domainAdaptationURL()

const service = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 10000
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    if (config.loading) {
      showLoading()
    }

    if (process.env.NODE_ENV === "development") {
      if (process.client) {
        config.headers["x-forwarded-host"] = window.location.host?.split(":")[0]
      }
    }

    config.headers = {
      Accept: "*/*",
      "Content-Type": "application/x-www-form-urlencoded",
      ...config.headers,
      "domain-flag": getDomainFlag()
    }

    return config
  },
  error => {
    Logger.error(`[ Interface ] [ ${error.config.url} ] Request Error: `, error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    const config = response.config
    if (config.loading) {
      hideLoading()
    }

    if (res.code === "0000") {
      if (config.cache) {
        const cacheKey = config.url + JSON.stringify(config.params || config.data)
        // 将响应数据缓存
        const maxAge = typeof config.cache === "number" ? config.cache : undefined
        LruCache.set(cacheKey, res, maxAge)
      }
      return Promise.resolve(res)
    } else if (res.code === "1002") {
      // 登录过期
      Logger.error(`[ 登录过期 ] [ ${config.url} ]  `)
      if (messageIns) {
        messageIns.close()
      }
      messageIns = Message({
        message: Vue.prototype.$translate("common.loginExpired"),
        type: "warning",
        duration: 5 * 1000
      })
      reSetLogin()
      setTimeout(() => {
        window.location.reload()
      }, 5000)
      return Promise.reject(res)
    } else if (res.code === "1003") {
      // 未登录处理
      Logger.error(`[ 未登录处理 ] [ ${config.url} ]  `)
      openPop()
      return Promise.reject(res)
    }
    if (!config.shouldErrorHandler) {
      errorHandler(res, response.status)
    }
    return Promise.reject(res)
  },
  error => {
    const response = error.response
    const config = error.config
    if (config.loading) {
      hideLoading()
    }
    if (response && response.data) {
      if (!config.shouldErrorHandler) {
        errorHandler(response.data, response.status)
      }
    }
    if (response) {
      const { status, data } = response
      let logMessage = `[ Interface ] [${config.baseURL}] [ ${config.url} ]\n`
      if (config.data) {
        logMessage += `Request Data: ${JSON.stringify(config.data)}\n`
      }
      if (config.params) {
        logMessage += `Request Params: ${JSON.stringify(config.params)}\n`
      }
      if (config.headers) {
        logMessage += `Request Headers: ${JSON.stringify(config.headers)}\n`
      }
      logMessage += `Response Error - Status: ${status}\n`
      logMessage += `Response Data: ${JSON.stringify(data)}\n`
      Logger.error(logMessage)
    } else {
      Logger.error(`[ Interface ] [ ${config.url} ] Response Error: No response received: `, error)
    }
    return Promise.reject(error)
  }
)

export const request = config => {
  if (config.cache) {
    const cacheKey = config.url + JSON.stringify(config.params || config.data)
    const cachedResponse = LruCache.get(cacheKey)
    if (cachedResponse) {
      return Promise.resolve(cachedResponse)
    }
  }

  return new Promise((resolve, reject) => {
    service
      .request(config)
      .then(res => {
        resolve(res)
      })
      .catch(err => {
        reject(err)
      })
  })
}

export function Post(url, data, config) {
  const { comm: commReq = {} } = (process.client && storage.get("commData")) || { comm: getCommonParam() }
  data = QS.stringify({
    ...data,
    comm: JSON.stringify({
      ...commReq
    })
  })
  return request({
    url,
    method: "post",
    data,
    ...config
  })
}

export function Get(url, data, config) {
  data = {
    ...data,
    comm: getCommonParam()
  }
  return request({
    url,
    method: "get",
    params: data,
    ...config
  })
}

export function postJSON(url, data, config) {
  const { comm: commReq = {} } = (process.client && storage.get("commData")) || { comm: getCommonParam() }
  data = {
    ...data,
    comm: commReq
  }

  return request({
    url,
    method: "post",
    data,
    ...config,
    headers: {
      ...config?.headers,
      "Content-Type": "application/json;charset=UTF-8"
    }
  })
}
